import type {
  AdminInsights,
  AdminAlert,
  AlertCounts,
  ClientRow,
  Invoice,
  ProjectRow,
  Quote,
} from "./types";

const DAY = 24 * 60 * 60 * 1000;

function daysBetween(a: Date, b: Date) {
  return Math.round((a.getTime() - b.getTime()) / DAY);
}

function monthKey(d: Date) {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

/**
 * Pure builder — derives the executive dashboard payload from already-loaded
 * lists. Shared between mock and cloud adapters to keep behaviour identical.
 */
export function buildAdminInsights(input: {
  clients: ClientRow[];
  projects: ProjectRow[];
  invoices: Invoice[];
  quotes: Quote[];
}): AdminInsights {
  const { clients, projects, invoices, quotes } = input;
  const now = new Date();

  // ── KPIs ────────────────────────────────────────────────────────────────
  const openInvoices = invoices.filter((i) => i.status !== "paid" && i.status !== "void");
  const overdueInv = openInvoices.filter((i) => i.due_date && new Date(i.due_date) < now);
  const outstanding = openInvoices.reduce((s, i) => s + Number(i.amount ?? 0), 0);
  const revenue = invoices
    .filter((i) => i.status === "paid")
    .reduce((s, i) => s + Number(i.amount ?? 0), 0);
  const activeProjects = projects.filter((p) => p.status !== "archived").length;
  const quotesOpen = quotes.filter((q) => q.status === "draft" || q.status === "sent").length;

  // ── Pipeline counts ─────────────────────────────────────────────────────
  const tally = (rows: { status: string }[]) => {
    const map = new Map<string, number>();
    rows.forEach((r) => map.set(r.status, (map.get(r.status) ?? 0) + 1));
    return Array.from(map, ([status, count]) => ({ status, count })).sort((a, b) => b.count - a.count);
  };

  // ── Revenue by month (last 6 COMPLETED months) ─────────────────────────
  // We end the trend at the previous full month so an empty current month
  // (e.g. day 2 of June) doesn't make the curve crash to 0 and produce a
  // misleading "-100% vs last month" delta. As soon as the month flips,
  // the new month becomes the latest data point.
  const months: { month: string; total: number }[] = [];
  for (let i = 6; i >= 1; i--) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    months.push({ month: monthKey(d), total: 0 });
  }
  invoices
    .filter((i) => i.status === "paid" && i.paid_at)
    .forEach((i) => {
      const k = monthKey(new Date(i.paid_at as string));
      const bucket = months.find((m) => m.month === k);
      if (bucket) bucket.total += Number(i.amount ?? 0);
    });

  // ── Top outstanding clients ─────────────────────────────────────────────
  const outstandingByClient = new Map<string, number>();
  openInvoices.forEach((i) => {
    if (!i.client_id) return;
    outstandingByClient.set(i.client_id, (outstandingByClient.get(i.client_id) ?? 0) + Number(i.amount ?? 0));
  });
  const topOutstanding = Array.from(outstandingByClient, ([client_id, amt]) => {
    const client = clients.find((c) => c.id === client_id);
    return {
      client_id,
      company_name: client?.company_name ?? "—",
      outstanding: amt,
    };
  })
    .sort((a, b) => b.outstanding - a.outstanding)
    .slice(0, 5);

  // ── Recent activity (mixed feed) ────────────────────────────────────────
  const activity: AdminInsights["recentActivity"] = [];
  invoices
    .filter((i) => i.status === "paid" && i.paid_at)
    .forEach((i) =>
      activity.push({
        id: `inv-${i.id}`,
        kind: "invoice_paid",
        title: i.invoice_number ?? `INV-${i.id.slice(0, 6)}`,
        subtitle: i.clients?.company_name ?? undefined,
        at: i.paid_at as string,
        href: `/os/admin/invoices?invoice=${i.id}`,
      }),
    );
  projects.forEach((p) => {
    if (p.last_update) {
      activity.push({
        id: `proj-${p.id}-${p.last_update}`,
        kind: "project_update",
        title: p.name,
        subtitle: p.clients?.company_name ?? undefined,
        at: p.last_update,
        href: `/os/admin/projects/${p.id}`,
      });
    }
  });
  quotes
    .filter((q) => q.status === "sent")
    .forEach((q) =>
      activity.push({
        id: `quote-${q.id}`,
        kind: "quote_sent",
        title: q.title,
        subtitle: q.clients?.company_name ?? q.prospect_name ?? undefined,
        at: q.created_at,
        href: `/os/admin/quotes`,
      }),
    );
  clients.forEach((c) =>
    activity.push({
      id: `cli-${c.id}`,
      kind: "client_new",
      title: c.company_name,
      subtitle: c.contact_name ?? undefined,
      at: c.created_at,
      href: `/os/admin/clients/${c.id}`,
    }),
  );
  const recentActivity = activity
    .filter((a) => a.at)
    .sort((a, b) => b.at.localeCompare(a.at))
    .slice(0, 8);

  // ── Alerts ──────────────────────────────────────────────────────────────
  const alerts: AdminAlert[] = [];

  overdueInv.forEach((i) => {
    const days = i.due_date ? daysBetween(now, new Date(i.due_date)) : 0;
    alerts.push({
      id: `inv-overdue-${i.id}`,
      section: "invoices",
      severity: days > 14 ? "critical" : "warning",
      kind: "invoice_overdue",
      title: i.invoice_number ?? `INV-${i.id.slice(0, 6)}`,
      subtitle: i.clients?.company_name ?? undefined,
      href: `/os/admin/invoices?invoice=${i.id}`,
      amount: Number(i.amount ?? 0),
      currency: i.currency,
      date: i.due_date,
    });
  });

  projects
    .filter((p) => p.status !== "archived" && p.status !== "completed")
    .forEach((p) => {
      // Stale: no update in 14+ days (or never updated and created 14+ days ago)
      const lastTouch = p.last_update ?? p.created_at;
      if (lastTouch && daysBetween(now, new Date(lastTouch)) >= 14) {
        alerts.push({
          id: `proj-stale-${p.id}`,
          section: "projects",
          severity: "warning",
          kind: "project_stale",
          title: p.name,
          subtitle: p.clients?.company_name ?? undefined,
          href: `/os/admin/projects/${p.id}`,
          date: lastTouch,
        });
      }
      // Unassigned
      if ((p.team_count ?? 0) === 0) {
        alerts.push({
          id: `proj-unassigned-${p.id}`,
          section: "projects",
          severity: "warning",
          kind: "project_unassigned",
          title: p.name,
          subtitle: p.clients?.company_name ?? undefined,
          href: `/os/admin/projects/${p.id}`,
        });
      }
      // Due soon
      if (p.due_date) {
        const d = daysBetween(new Date(p.due_date), now);
        if (d >= 0 && d <= 7) {
          alerts.push({
            id: `proj-duesoon-${p.id}`,
            section: "projects",
            severity: "info",
            kind: "project_due_soon",
            title: p.name,
            subtitle: p.clients?.company_name ?? undefined,
            href: `/os/admin/projects/${p.id}`,
            date: p.due_date,
          });
        } else if (d < 0) {
          alerts.push({
            id: `proj-overdue-${p.id}`,
            section: "projects",
            severity: "critical",
            kind: "project_overdue",
            title: p.name,
            subtitle: p.clients?.company_name ?? undefined,
            href: `/os/admin/projects/${p.id}`,
            date: p.due_date,
          });
        }
      }
    });

  quotes
    .filter((q) => q.status === "sent" && q.valid_until)
    .forEach((q) => {
      const d = daysBetween(new Date(q.valid_until as string), now);
      if (d < 0) {
        alerts.push({
          id: `quote-expired-${q.id}`,
          section: "quotes",
          severity: "warning",
          kind: "quote_expired",
          title: q.title,
          subtitle: q.clients?.company_name ?? q.prospect_name ?? undefined,
          href: `/os/admin/quotes`,
          date: q.valid_until,
          amount: Number(q.total ?? 0),
          currency: q.currency,
        });
      } else if (d <= 7) {
        alerts.push({
          id: `quote-expiring-${q.id}`,
          section: "quotes",
          severity: "info",
          kind: "quote_expiring",
          title: q.title,
          subtitle: q.clients?.company_name ?? q.prospect_name ?? undefined,
          href: `/os/admin/quotes`,
          date: q.valid_until,
          amount: Number(q.total ?? 0),
          currency: q.currency,
        });
      }
    });

  // Accepted quote without project linkage → won't auto-bill until linked.
  quotes
    .filter((q) => q.status === "accepted" && !q.project_id)
    .forEach((q) => {
      alerts.push({
        id: `quote-unlinked-${q.id}`,
        section: "quotes",
        severity: "warning",
        kind: "quote_accepted_unlinked",
        title: q.title,
        subtitle: q.clients?.company_name ?? q.prospect_name ?? undefined,
        href: `/os/admin/quotes`,
        amount: Number(q.monthly_fee ?? q.total ?? 0),
        currency: q.currency,
      });
    });

  // Severity ordering (critical first)
  const sevOrder = { critical: 0, warning: 1, info: 2 } as const;
  alerts.sort((a, b) => sevOrder[a.severity] - sevOrder[b.severity]);

  const alertCounts: AlertCounts = {
    invoices: alerts.filter((a) => a.section === "invoices").length,
    projects: alerts.filter((a) => a.section === "projects").length,
    quotes: alerts.filter((a) => a.section === "quotes").length,
    clients: alerts.filter((a) => a.section === "clients").length,
    total: alerts.length,
  };

  return {
    stats: {
      clients: clients.length,
      activeProjects,
      openInvoices: openInvoices.length,
      overdue: overdueInv.length,
      revenue,
      quotesOpen,
      outstanding,
    },
    pipeline: {
      clientsByStatus: tally(clients),
      projectsByStatus: tally(projects.filter((p) => p.status !== "archived")),
    },
    revenueByMonth: months,
    topOutstanding,
    recentActivity,
    alerts: alerts.slice(0, 12),
    alertCounts,
  };
}